import { all, put, takeLatest, call } from 'redux-saga/effects';
import { WebhookActionTypes } from 'types';
import {
  getWebhookSubscriptionsSuccess,
  getWebhookSubscriptionsFailure,
  addWebhookSubscriptionSuccess,
  addWebhookSubscriptionFailure,
  updateWebhookSubscriptionSuccess,
  updateWebhookSubscriptionFailure,
  deleteWebhookSubscriptionSuccess,
  deleteWebhookSubscriptionFailure,
  getWebhookNotificationHistorySuccess,
  getWebhookNotificationHistoryFailure,
  getWebhookNotificationDetailsSuccess,
  getWebhookNotificationDetailsFailure,
  redeliverWebhookNotificationSuccess,
  redeliverWebhookNotificationFailure
} from './actions';
import {
  getWebhookSubscriptions,
  addNewWebhookSubscriptions,
  updatedWebhookSubscription,
  deleteWebhookSubscription,
  getWebhookNotificationHistory,
  getWebhookNotificationDetails,
  redeliverWebhookNotification
} from 'services/webhook.service';

function* handleGetWebhookSubscriptions(request: any) {
  try {
    const response = yield getWebhookSubscriptions(request.payload);
    if (response.error) {
      return yield put(getWebhookSubscriptionsFailure(response.error.message));
    }
    const subscriptions = response?.Subscriptions;
    if (Array.isArray(subscriptions)) {
      const personalList = subscriptions.filter(
        (s) => s.organisationId === null
      );
      const allList = subscriptions.filter((s) => s.organisationId !== null);
      yield put(
        getWebhookSubscriptionsSuccess({
          personalList,
          allList
        })
      );
    } else {
      yield put(
        getWebhookSubscriptionsFailure('Unable to get webhooks subscriptions')
      );
    }
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(getWebhookSubscriptionsFailure(errMsg));
  }
}

function* handleAddWebhookSubscription(request: any) {
  try {
    const response = yield addNewWebhookSubscriptions(request.payload);
    if (response.error) {
      return yield put(addWebhookSubscriptionFailure(response.error.message));
    }
    const subscription = response?.WebhookSubscription;
    if (subscription) {
      yield put(addWebhookSubscriptionSuccess(subscription));
    } else {
      yield put(
        addWebhookSubscriptionFailure('Unable to add webhooks subscription')
      );
    }
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(addWebhookSubscriptionFailure(errMsg));
  }
}

function* handleUpdateWebhookSubscription(request: any) {
  try {
    const response = yield updatedWebhookSubscription(
      request.payload.subscriptionId,
      request.payload.data
    );
    if (response.error) {
      return yield put(
        updateWebhookSubscriptionFailure(response.error.message)
      );
    }
    const subscription = response?.WebhookSubscription;
    if (subscription) {
      yield put(updateWebhookSubscriptionSuccess(subscription));
    } else {
      yield put(
        updateWebhookSubscriptionFailure(
          'Unable to update webhooks subscription'
        )
      );
    }
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(updateWebhookSubscriptionFailure(errMsg));
  }
}

function* handleDeleteWebhookSubscription(request: any) {
  try {
    yield deleteWebhookSubscription(request.payload);

    yield put(deleteWebhookSubscriptionSuccess());
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(deleteWebhookSubscriptionFailure(errMsg));
  }
}

export function* handleGetWebhookNotificationHistory(request: any) {
  try {
    const response = yield call(
      getWebhookNotificationHistory,
      request?.payload
    );

    if (response.error) {
      throw new Error(response.error.message);
    }
    yield put(getWebhookNotificationHistorySuccess(response));
  } catch (error) {
    yield put(getWebhookNotificationHistoryFailure(error.message));
  }
}

export function* handleGetWebhookNotificationDetails(request: any) {
  try {
    const response = yield call(
      getWebhookNotificationDetails,
      request?.payload?.subscriptionId,
      request?.payload?.notificationId
    );
    if (response.error) {
      throw new Error(response.error.message);
    }
    yield put(getWebhookNotificationDetailsSuccess(response));
  } catch (error) {
    yield put(getWebhookNotificationDetailsFailure(error.message));
  }
}

export function* handleRedeliverWebhookNotification(request: any) {
  try {
    const response = yield call(redeliverWebhookNotification, request?.payload);
    if (response.error) {
      throw new Error(response.error.message);
    }
    yield put(redeliverWebhookNotificationSuccess(response));
  } catch (error) {
    yield put(redeliverWebhookNotificationFailure(error.message));
  }
}

function* watchGetWebhookSubscriptions() {
  yield takeLatest(
    WebhookActionTypes.GET_WEB_HOOK_SUBSCRIPTIONS_START,
    handleGetWebhookSubscriptions
  );
}

function* watchAddWebhookSubscription() {
  yield takeLatest(
    WebhookActionTypes.ADD_WEB_HOOK_SUBSCRIPTION_START,
    handleAddWebhookSubscription
  );
}

function* watchUpdateWebhookSubscription() {
  yield takeLatest(
    WebhookActionTypes.UPDATE_WEB_HOOK_SUBSCRIPTION_START,
    handleUpdateWebhookSubscription
  );
}

function* watchDeleteWebhookSubscription() {
  yield takeLatest(
    WebhookActionTypes.DELETE_WEB_HOOK_SUBSCRIPTION_START,
    handleDeleteWebhookSubscription
  );
}

function* watchGetWebhookNotificationHistory() {
  yield takeLatest(
    WebhookActionTypes.GET_WEB_HOOK_NOTIFICATION_HISTORY_START,
    handleGetWebhookNotificationHistory
  );
}

function* watchGetWebhookNotificationDetails() {
  yield takeLatest(
    WebhookActionTypes.GET_WEB_HOOK_NOTIFICATION_DETAILS_START,
    handleGetWebhookNotificationDetails
  );
}

function* watchRedeliverWebhookNotification() {
  yield takeLatest(
    WebhookActionTypes.REDELIVER_WEB_HOOK_NOTIFICATION_START,
    handleRedeliverWebhookNotification
  );
}

function* WebhookSagas() {
  yield all([
    call(watchGetWebhookSubscriptions),
    call(watchAddWebhookSubscription),
    call(watchUpdateWebhookSubscription),
    call(watchDeleteWebhookSubscription),
    call(watchGetWebhookNotificationHistory),
    call(watchGetWebhookNotificationDetails),
    call(watchRedeliverWebhookNotification)
  ]);
}

export default WebhookSagas;
